import { DEFAULT_SCHEMAS } from '../../modules/KLogicSchemas.js';
import { findDataPathForColumn, getColumnLabel, OFF_PATH_DISPLAY_TYPES } from './OverviewColumns.js';

/** @import { SimpleLogicFieldData } from '../../features/filters/simple-filter-editor/BuildSimpleLogicFieldData.js' */
/** @import { FilterField } from '../../features/filters/simple-filter-editor/SimpleFilterEditorLogic.js' */

// The context the overview filter runs against: one job_permissions/{companyId}/jobs entry, not a whole job
// Only what that summary stores belongs here, since the full JOB schema would offer fields that match nothing
export const JOB_SUMMARY_SCHEMA = {
  name: { $type: 'text', $label: 'Job Name', $group: 'Job' },
  metadata: DEFAULT_SCHEMAS.JOB.metadata
};

/**
 * Collect the field behind each configured column, labelled the way that column's header is.
 * @param {FilterField[]} fields
 * @param {Object<string, string>} dataPathsById
 * @param {object[]} overviewAttributes
 * @param {Object<string, object>} attributeModels
 * @returns {FilterField[]}
 */
function collectColumnFields(fields, dataPathsById, overviewAttributes, attributeModels) {
  const fieldsByDataPath = {};
  for (const field of fields) {
    fieldsByDataPath[dataPathsById[field.id]] = field;
  }

  const columnFields = [];
  const collectedFieldIds = new Set();
  for (const column of overviewAttributes || []) {
    if (OFF_PATH_DISPLAY_TYPES.includes(column.display_as)) continue;

    const dataPath = findDataPathForColumn(column.attribute);
    const field = dataPath == null ? null : fieldsByDataPath[dataPath];
    // A column the editor offers no field for is skipped, which is how a retired attribute falls out
    if (field == null || collectedFieldIds.has(field.id)) continue;
    collectedFieldIds.add(field.id);
    const label = getColumnLabel(column.attribute, { overviewAttributes, attributeModels });
    columnFields.push({ ...field, label, group: 'Columns' });
  }
  return columnFields;
}

/**
 * Prepend the field behind each configured column to the editor's field data, labelled the way its header is.
 * @param {SimpleLogicFieldData} fieldData
 * @param {object[]} overviewAttributes The overview's configured columns, in the order they are displayed.
 * @param {Object<string, object>} attributeModels
 * @returns {SimpleLogicFieldData} The same fields and paths, with the column-backed ones moved to the front.
 */
export function prependWithColumnFilterFields(fieldData, overviewAttributes, attributeModels) {
  const fields = fieldData?.fields || [];
  const dataPathsById = fieldData?.dataPathsById || {};

  const columnFields = collectColumnFields(fields, dataPathsById, overviewAttributes, attributeModels);
  const columnFieldIds = new Set(columnFields.map((field) => field.id));
  const remainingFields = fields.filter((field) => !columnFieldIds.has(field.id));

  // Order carries the grouping here, since the editor groups by first appearance rather than sorting
  return { fields: [...columnFields, ...remainingFields], dataPathsById };
}
